import { type TripType } from '@utils/trip';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { type ReserveType } from '@utils/itinerary';

const useTripSearchForm = <T extends TripSearchForm = TripSearchForm>(
    isChangeSearch: boolean,
    staticReserveType?: ReserveType
) => {
    const rest = useSearchForm<T>(isChangeSearch, staticReserveType);
    const analyticsStore = useAnalyticsStore();
    let currentSearchForm = useCurrentSearchForm<T>();
    if (staticReserveType) {
        currentSearchForm = computed(() =>
            useSearchFormStore().getFormModule(staticReserveType)
        );
    }
    const { tripTypeOptions, tripType } = moduleToRefs(currentSearchForm.value);

    const origin = computed(() => currentSearchForm.value.origin);
    const onUpdateOrigin = (location: T['origin']) => {
        currentSearchForm.value.setOrigin(location);
        analyticsStore.searchInputSearchCount.citySelect++;
        currentSearchForm.value.loadPrices();
    };

    const onUpdateTripType = (tripType: TripType) => {
        currentSearchForm.value.setTripTypeAndCheckDate(tripType);
        currentSearchForm.value.loadPrices();
    };

    const origins = computed(() => currentSearchForm.value.filteredOrigins);

    const onSearch = (token: string, target: 'origin' | 'destination') => {
        currentSearchForm.value.searchLocations(token.trim(), {
            target,
            mode: currentSearchForm.value.searchMode,
        });
    };

    const onSwap = () => {
        currentSearchForm.value.swap();
    };

    const swapDisabled = computed(() => !origin.value || !rest.destination);
    const onSubmit = async () => {
        currentSearchForm.value.submitGuard();
        return await rest.onSubmit();
    };

    return {
        ...rest,
        onSubmit,
        onSwap,
        origin,
        'onUpdate:origin': onUpdateOrigin,
        swapDisabled,
        tripTypeOptions,
        tripType,
        'onUpdate:tripType': onUpdateTripType,
        origins,
        onSearch,
    };
};

export default useTripSearchForm;
